import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import OrganicDivider from "@/components/base/OrganicDivider";
import SectionHeading from "@/components/base/SectionHeading";
import ScrollReveal from "@/components/base/ScrollReveal";
import { credibilityStats } from "@/mocks/credibility";
import { whyChooseItems } from "@/mocks/whyChoose";

function AnimatedCounter({ value, suffix, prefix = "" }: { value: string; suffix: string; prefix?: string }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            if (!isNaN(Number(value))) {
              const target = Number(value);
              let current = 0;
              const step = Math.max(1, Math.floor(target / 40));
              const timer = setInterval(() => {
                current += step;
                if (current >= target) { current = target; clearInterval(timer); }
                setDisplay(String(current));
              }, 30);
            } else {
              setDisplay(value);
            }
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

const missionVisionValues = [
  {
    icon: "ri-focus-3-line",
    title: "Missão",
    body: "Produzir alimentos de qualidade que unem sabor, praticidade e segurança, superando as expectativas de clientes e parceiros em cada entrega.",
    color: "primary" as const,
  },
  {
    icon: "ri-eye-line",
    title: "Visão",
    body: "Ser referência em alimentação natural e prática no Nordeste, expandindo nossa presença com excelência e inovação constante.",
    color: "accent" as const,
  },
  {
    icon: "ri-heart-line",
    title: "Valores",
    body: "Qualidade em cada ingrediente, compromisso com prazos, respeito aos parceiros, transparência nas relações e paixão pelo que fazemos.",
    color: "secondary" as const,
  },
];

const timeline = [
  { year: "2005", title: "Fundação", body: "A Big Mania nasce em Fortaleza com a missão de oferecer sanduíches naturais frescos e saborosos." },
  { year: "2010", title: "Expansão", body: "Ampliamos nossa linha de produtos e iniciamos o fornecimento para supermercados e redes varejistas." },
  { year: "2015", title: "Produção Própria", body: "Inauguramos nossa unidade de produção própria com estrutura moderna e controle total de qualidade." },
  { year: "2020", title: "Eventos", body: "Lançamos nossa divisão de eventos corporativos, com cardápios personalizados e serviço completo." },
  { year: "2025", title: "Consolidação", body: "Mais de 1000 clientes atendidos, presença consolidada no mercado cearense e nove sabores de sanduíches." },
];

const colorMap: Record<string, { bg: string; icon: string; border: string }> = {
  primary: { bg: "bg-primary-100", icon: "text-primary-600", border: "hover:border-primary-300/50" },
  accent: { bg: "bg-accent-100", icon: "text-accent-600", border: "hover:border-accent-300/50" },
  secondary: { bg: "bg-secondary-100", icon: "text-secondary-600", border: "hover:border-secondary-300/50" },
};

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="Sobre Nós"
        title={<>Conheça a <em className="not-italic text-accent-400">Big Mania</em></>}
        intro="Desde 2005 transformando ingredientes selecionados em alimentos que unem sabor, qualidade e praticidade. Somos referência em Fortaleza na fabricação de sanduíches naturais, mini pastéis e soluções completas de alimentação."
        image="https://storage.readdy-site.link/project_files/e2268d4a-73e3-4367-9d65-df24b53a0559/9a3f53c3-75a1-4927-b0c5-0726bffcc4a3_compressed_F-324.webp"
        imageAlt="Produção artesanal de sanduíches naturais na cozinha da Big Mania"
        primaryLabel="Falar pelo WhatsApp"
        primaryHref="https://wa.me/558534942701"
        secondaryLabel="Ver Cardápio"
        secondaryTo="/cardapio"
      />

      <OrganicDivider variant="wave" fill="background-50" />

      {/* Nossa História */}
      <section className="relative w-full py-20 md:py-28 bg-background-100 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ScrollReveal>
              <div className="rounded-2xl overflow-hidden bg-background-200/40">
                <img
                  src="https://storage.readdy-site.link/project_files/e2268d4a-73e3-4367-9d65-df24b53a0559/213c86da-fbae-4d52-8ca2-b5a82b94b0fc_compressed_WhatsApp-Image-2026-07-20-at-22.webp"
                  alt="Fundador da Big Mania na cozinha de produção"
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover object-top"
                />
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.15}>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-4">
                Nossa História
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                Uma história de{" "}
                <span className="text-primary-600">sabor</span>{" "}
                e dedicação
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed text-base md:text-lg">
                <p>
                  A Big Mania nasceu em 2005, com um propósito simples: oferecer sanduíches naturais que fossem ao mesmo tempo deliciosos, saudáveis e acessíveis. O que começou como uma operação familiar cresceu e se transformou em uma indústria alimentícia de referência no Ceará.
                </p>
                <p>
                  Com produção própria e processos cuidadosamente desenvolvidos, trabalhamos com ingredientes frescos selecionados diariamente. Cada sanduíche, cada mini pastel, cada produto que sai da nossa cozinha carrega o compromisso com a qualidade que nos trouxe até aqui.
                </p>
                <p>
                  Hoje, atendemos consumidores, supermercados, escolas, hotéis, academias, empresas e eventos — sempre com o mesmo cuidado artesanal do primeiro dia.
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-800">
                  <i className="ri-map-pin-2-line text-primary-600"></i>
                  Fortaleza, Ceará
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-800">
                  <i className="ri-time-line text-accent-600"></i>
                  Desde 2005
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <OrganicDivider variant="wave-invert" fill="background-100" />

      {/* Missão, Visão e Valores */}
      <section className="relative w-full py-20 md:py-28 bg-background-50 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <ScrollReveal>
            <SectionHeading
              eyebrow="Propósito"
              title={<>O que nos <em className="not-italic text-accent-600">move</em></>}
              intro="Nossa essência está em três pilares que guiam cada decisão e cada produto que entregamos."
              align="center"
              className="mb-14 md:mb-20"
            />
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.1} staggerChildren staggerDelay={0.12}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {missionVisionValues.map((item) => (
                <div
                  key={item.title}
                  className={`p-8 rounded-2xl bg-background-100 border border-background-200/60 text-center group card-glow ${colorMap[item.color].border} transition-all duration-500 hover:-translate-y-1.5`}
                >
                  <div className={`w-14 h-14 rounded-2xl ${colorMap[item.color].bg} flex items-center justify-center mx-auto mb-5`}>
                    <i className={`${item.icon} ${colorMap[item.color].icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-foreground-950 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <OrganicDivider variant="wave" fill="background-50" />

      {/* Linha do Tempo */}
      <section className="relative w-full py-20 md:py-28 bg-background-100 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <ScrollReveal>
            <SectionHeading
              eyebrow="Trajetória"
              title={<>Nossa <em className="not-italic text-primary-600">jornada</em></>}
              intro="Mais de 20 anos de história, crescimento e compromisso com a qualidade."
              className="mb-14 md:mb-20"
            />
          </ScrollReveal>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-background-300"></div>
            <div className="space-y-10 md:space-y-14">
              {timeline.map((item, idx) => (
                <ScrollReveal key={item.year} direction={idx % 2 === 0 ? "right" : "left"} delay={0.05 * idx}>
                  <div className={`relative flex items-start gap-6 md:gap-10 ${idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? "md:text-right" : "md:text-left"} pl-12 md:pl-0`}>
                      <span className="inline-block text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                        {item.year}
                      </span>
                      <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed max-w-md">{item.body}</p>
                    </div>
                    <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-100 mt-1.5 z-10"></div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <OrganicDivider variant="wave-invert" fill="background-100" />

      {/* Nossos Números */}
      <section className="relative w-full py-20 md:py-28 bg-background-50 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <ScrollReveal>
            <SectionHeading
              eyebrow="Números"
              title={<>Big Mania <em className="not-italic text-accent-600">em dados</em></>}
              intro="Números que refletem nossa trajetória e o compromisso com a excelência."
              align="center"
              className="mb-14 md:mb-20"
            />
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.1} staggerChildren staggerDelay={0.1}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {credibilityStats.map((stat) => (
                <div
                  key={stat.label}
                  className="relative p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 text-center group card-glow hover:border-primary-300/60 transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-foreground-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <OrganicDivider variant="wave" fill="background-50" />

      {/* Por que escolher */}
      <section className="relative w-full py-20 md:py-28 bg-background-100 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <ScrollReveal>
            <SectionHeading
              eyebrow="Diferenciais"
              title={<>Por que escolher a <em className="not-italic text-primary-600">Big Mania</em></>}
              intro="São muitos os motivos que fazem da Big Mania a escolha certa para sua alimentação e seu negócio."
              align="center"
              className="mb-14 md:mb-20"
            />
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.1} staggerChildren staggerDelay={0.08}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {whyChooseItems.map((item) => (
                <div
                  key={item.title}
                  className={`p-6 rounded-2xl bg-background-50 border border-background-200/60 group card-glow ${colorMap[item.color].border} transition-all duration-500 hover:-translate-y-1`}
                >
                  <div className={`w-10 h-10 rounded-xl ${colorMap[item.color].bg} flex items-center justify-center mb-4`}>
                    <i className={`${item.icon} ${colorMap[item.color].icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <CTASection
        eyebrow="Vamos conversar"
        subtitle="Fale com a gente pelo WhatsApp e descubra como a Big Mania pode atender você, sua empresa ou seu evento."
        primaryLabel="Falar pelo WhatsApp"
        primaryHref="https://wa.me/558534942701"
        secondaryLabel="Ver Cardápio"
        secondaryTo="/cardapio"
      />
    </>
  );
}